import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "react-router-dom";

import { gtmQueryKeys } from "../../gtm/hooks/gtmQueryKeys";
import type { GtmPlay } from "../../gtm/api/GtmApi";
import {
  getAskCrmRecordContext,
  subscribeAskCrmRecordContext,
  type AskCrmRecordContext,
} from "../askCrmRecordContextStore";
import { deriveAskPageContext, mergeAskPageContext } from "../utils/askPageContext";
import type { AskPageContext } from "../types/chat.types";

/**
 * Derives Ask Type anything page context (stories 12.1 / 12.5).
 * GTM routes win; else published CRM detail record; else workspace.
 */
export function useAskPageContext(): AskPageContext {
  const location = useLocation();
  const { projectId = "", playId = "" } = useParams<{
    projectId?: string;
    playId?: string;
  }>();
  const queryClient = useQueryClient();
  const [crmRecord, setCrmRecord] = useState<AskCrmRecordContext | null>(() =>
    getAskCrmRecordContext()
  );

  useEffect(() => subscribeAskCrmRecordContext(() => setCrmRecord(getAskCrmRecordContext())), []);

  return useMemo(() => {
    let playName: string | null = null;
    if (projectId && playId) {
      const cached = queryClient.getQueryData<GtmPlay>(gtmQueryKeys.play(projectId, playId));
      playName = cached?.name ?? null;
    }
    const fromRoute = deriveAskPageContext({
      pathname: location.pathname,
      search: location.search,
      playName,
    });
    return mergeAskPageContext({
      pathname: location.pathname,
      routeContext: fromRoute,
      crmRecord,
    });
  }, [crmRecord, location.pathname, location.search, playId, projectId, queryClient]);
}
